import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { 
    ShieldCheck,
    Shield,
    Star,
    FileText,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    Eye,
    RefreshCw,
    AlertTriangle,
    User,
    Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { cn } from '../lib/utils';

// Tier badge component
const TierBadge = ({ tier }) => {
    const tiers = {
        basic: { label: 'Basic', color: 'bg-gray-500/15 text-gray-400', icon: Shield },
        verified: { label: 'Verified', color: 'bg-blue-500/15 text-blue-400', icon: ShieldCheck },
        trusted: { label: 'Trusted', color: 'bg-amber-500/15 text-amber-400', icon: Star },
    };
    
    const t = tiers[tier] || tiers.basic;
    const Icon = t.icon;
    
    return (
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full", t.color)}>
            <Icon className="h-3 w-3" />
            {t.label}
        </span>
    );
};

// Status badge
const StatusBadge = ({ status }) => {
    const statuses = {
        pending: { label: 'Pending', color: 'bg-amber-500/15 text-amber-500', icon: Clock },
        approved: { label: 'Approved', color: 'bg-emerald-500/15 text-emerald-500', icon: CheckCircle },
        rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-500', icon: XCircle },
        resubmission_requested: { label: 'Resubmission', color: 'bg-orange-500/15 text-orange-500', icon: RefreshCw },
        revoked: { label: 'Revoked', color: 'bg-gray-500/15 text-gray-500', icon: XCircle },
    };
    
    const s = statuses[status] || statuses.pending;
    const Icon = s.icon;
    
    return (
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full", s.color)}>
            <Icon className="h-3 w-3" />
            {s.label}
        </span>
    );
};

const AdminVerifications = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [verifications, setVerifications] = useState([]);
    const [counts, setCounts] = useState({});
    const [activeTab, setActiveTab] = useState('pending');
    const [selectedVerification, setSelectedVerification] = useState(null);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [reviewAction, setReviewAction] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        loadVerifications();
    }, [activeTab]);

    const loadVerifications = async () => {
        setLoading(true);
        try {
            const status = activeTab === 'all' ? '' : activeTab;
            const response = await api.get(`/admin/verifications${status ? `?status=${status}` : ''}`);
            setVerifications(response.data.verifications || []);
            setCounts(response.data.counts || {});
        } catch (err) {
            console.error('Failed to load verifications:', err);
            toast.error('Failed to load verifications');
        } finally {
            setLoading(false);
        }
    };

    const openReviewDialog = (verification, action) => {
        setSelectedVerification(verification);
        setReviewAction(action);
        setRejectionReason('');
        setAdminNotes('');
        setReviewDialogOpen(true);
    };

    const handleReview = async () => {
        if (!selectedVerification || !reviewAction) return;
        
        if ((reviewAction === 'reject' || reviewAction === 'request_resubmission') && !rejectionReason) {
            toast.error('Please provide a reason');
            return;
        }

        setProcessing(true);
        try {
            await api.put(`/admin/verifications/${selectedVerification.id}`, {
                action: reviewAction,
                rejection_reason: rejectionReason || null,
                admin_notes: adminNotes || null,
            });
            
            const actionLabels = {
                approve: 'approved',
                reject: 'rejected',
                request_resubmission: 'requested resubmission',
                revoke: 'revoked'
            };
            
            toast.success(`Verification ${actionLabels[reviewAction]}`);
            setReviewDialogOpen(false);
            loadVerifications();
        } catch (err) {
            console.error('Failed to review verification:', err);
            toast.error(err.response?.data?.detail || 'Failed to process review');
        } finally {
            setProcessing(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const viewDocument = async (verificationId) => {
        try {
            const response = await api.get(`/admin/verifications/${verificationId}/document`);
            if (response.data.document_url) {
                window.open(`${process.env.REACT_APP_BACKEND_URL}${response.data.document_url}`, '_blank');
            }
        } catch (err) {
            toast.error('Failed to load document');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <ShieldCheck className="h-7 w-7 text-primary" />
                            Verification Reviews
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Review and approve contractor verification documents
                        </p>
                    </div>
                    <Button onClick={loadVerifications} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Refresh
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="bg-card border-border">
                        <CardContent className="p-4 text-center">
                            <Clock className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-foreground">{counts.pending || 0}</p>
                            <p className="text-xs text-muted-foreground">Pending</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="p-4 text-center">
                            <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-foreground">{counts.approved || 0}</p>
                            <p className="text-xs text-muted-foreground">Approved</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="p-4 text-center">
                            <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-foreground">{counts.rejected || 0}</p>
                            <p className="text-xs text-muted-foreground">Rejected</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border-border">
                        <CardContent className="p-4 text-center">
                            <FileText className="h-5 w-5 text-primary mx-auto mb-1" />
                            <p className="text-2xl font-bold text-foreground">{counts.total || 0}</p>
                            <p className="text-xs text-muted-foreground">Total</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="pending" data-testid="tab-pending">
                            Pending ({counts.pending || 0})
                        </TabsTrigger>
                        <TabsTrigger value="approved" data-testid="tab-approved">
                            Approved
                        </TabsTrigger>
                        <TabsTrigger value="rejected" data-testid="tab-rejected">
                            Rejected
                        </TabsTrigger>
                        <TabsTrigger value="all" data-testid="tab-all">
                            All
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value={activeTab} className="mt-4">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : verifications.length > 0 ? (
                            <div className="space-y-4">
                                {verifications.map((v) => (
                                    <Card key={v.id} className="bg-card border-border">
                                        <CardContent className="p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                {/* Left: Contractor Info */}
                                                <div className="flex items-start gap-4">
                                                    <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                                                        <FileText className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-semibold text-foreground">{v.company_name}</p>
                                                            <TierBadge tier={v.contractor_tier} />
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{v.contractor_email}</p>
                                                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                            <span className="flex items-center gap-1">
                                                                <FileText className="h-3 w-3" />
                                                                {v.type_label}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="h-3 w-3" />
                                                                {formatDate(v.submitted_at)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Status & Actions */}
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <StatusBadge status={v.status} />
                                                    
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => viewDocument(v.id)}
                                                        data-testid={`view-doc-${v.id}`}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        View
                                                    </Button>
                                                    
                                                    {v.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="default"
                                                                size="sm"
                                                                onClick={() => openReviewDialog(v, 'approve')}
                                                                data-testid={`approve-${v.id}`}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-1" />
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                onClick={() => openReviewDialog(v, 'reject')}
                                                                data-testid={`reject-${v.id}`}
                                                            >
                                                                <XCircle className="h-4 w-4 mr-1" />
                                                                Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                    
                                                    {v.status === 'approved' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => openReviewDialog(v, 'revoke')}
                                                            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                                            data-testid={`revoke-${v.id}`}
                                                        >
                                                            <AlertTriangle className="h-4 w-4 mr-1" />
                                                            Revoke
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Show rejection reason if rejected */}
                                            {v.status === 'rejected' && v.rejection_reason && (
                                                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                                    <p className="text-sm text-red-500">
                                                        <XCircle className="h-4 w-4 inline mr-1" />
                                                        {v.rejection_reason}
                                                    </p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-card border-border">
                                <CardContent className="p-12 text-center">
                                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                    <p className="text-muted-foreground">No verifications found</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Review Dialog */}
                <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {reviewAction === 'approve' && <CheckCircle className="h-5 w-5 text-emerald-500" />}
                                {reviewAction === 'reject' && <XCircle className="h-5 w-5 text-red-500" />}
                                {reviewAction === 'request_resubmission' && <RefreshCw className="h-5 w-5 text-orange-500" />}
                                {reviewAction === 'revoke' && <AlertTriangle className="h-5 w-5 text-red-500" />}
                                {reviewAction === 'approve' && 'Approve Verification'}
                                {reviewAction === 'reject' && 'Reject Verification'}
                                {reviewAction === 'request_resubmission' && 'Request Resubmission'}
                                {reviewAction === 'revoke' && 'Revoke Verification'}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedVerification?.company_name} - {selectedVerification?.type_label}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            {(reviewAction === 'reject' || reviewAction === 'request_resubmission' || reviewAction === 'revoke') && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Reason {reviewAction !== 'revoke' && '(Required)'}
                                    </label>
                                    <Textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder={
                                            reviewAction === 'reject' 
                                                ? "Explain why this document doesn't meet requirements..."
                                                : reviewAction === 'revoke'
                                                ? "Reason for revoking verification..."
                                                : "What should the contractor fix or improve..."
                                        }
                                        rows={3}
                                        data-testid="rejection-reason-input"
                                    />
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">
                                    Admin Notes (Optional)
                                </label>
                                <Textarea
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    placeholder="Internal notes about this review..."
                                    rows={2}
                                    data-testid="admin-notes-input"
                                />
                            </div>

                            {reviewAction === 'approve' && (
                                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                                        Approving this document may upgrade the contractor's verification tier.
                                    </p>
                                </div>
                            )}

                            {reviewAction === 'revoke' && (
                                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400">
                                        Revoking may downgrade the contractor's verification tier and reduce their review weight.
                                    </p>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleReview}
                                disabled={processing || ((reviewAction === 'reject' || reviewAction === 'request_resubmission') && !rejectionReason)}
                                variant={reviewAction === 'approve' ? 'default' : 'destructive'}
                                data-testid="confirm-review-btn"
                            >
                                {processing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        {reviewAction === 'approve' && 'Approve'}
                                        {reviewAction === 'reject' && 'Reject'}
                                        {reviewAction === 'request_resubmission' && 'Request Resubmission'}
                                        {reviewAction === 'revoke' && 'Revoke'}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminVerifications;
